import React from "react";
import { BRAND } from "@/lib/api";

/**
 * Reusable header for all print pages.
 * Shows hospital logo + branch logo side-by-side when both exist.
 * Falls back to BRAND.icon when none uploaded.
 *
 * Props:
 *   hospital  — { name, logo_url, gstin, contact, address }
 *   branch    — { name, code, address, logo_url }
 *   subtitle  — e.g. "Tax Invoice", "Discharge Summary", "Operative Record"
 *   right     — JSX rendered on the right side of the header (invoice no., date, etc.)
 *   compact   — when true, uses smaller logo sizes (good for A5 / receipts)
 */
export default function PrintHeader({ hospital, branch, subtitle, right, compact = false }) {
  const hospitalLogo = hospital?.logo_url;
  const branchLogo = branch?.logo_url;
  const bothDistinct = !!(hospitalLogo && branchLogo && branchLogo !== hospitalLogo);

  const sizeBig = compact ? "h-10 w-10" : "h-14 w-14";
  const sizeFallback = compact ? "h-9 w-9" : "h-12 w-12";

  return (
    <div className="flex items-start justify-between border-b-2 border-brand pb-3">
      <div className="flex items-center gap-3">
        {/* Hospital logo (always primary). If none uploaded → CareO icon */}
        {hospitalLogo ? (
          <img src={hospitalLogo} alt="" className={`${sizeBig} rounded-md object-contain`} />
        ) : (
          <img src={BRAND.icon} alt="" className={sizeFallback} />
        )}
        {/* Branch logo — shown only if distinct from hospital */}
        {bothDistinct && (
          <img src={branchLogo} alt="" className={`${sizeBig} rounded-md object-contain border-l border-border pl-2`} />
        )}

        <div>
          <div className={`font-heading ${compact ? "text-lg" : "text-xl"} font-semibold text-brand`}>
            {hospital?.name || BRAND.name}
          </div>
          {subtitle && <div className="text-[11px] text-ink-muted">{subtitle}</div>}
          <div className="text-[10px] uppercase tracking-wider text-ink-muted">{BRAND.tagline}</div>
          {branch?.name && (
            <div className="text-[11px] text-ink-muted">
              {branch.name}{branch.code ? ` · ${branch.code}` : ""}{branch.address ? ` · ${branch.address}` : ""}
            </div>
          )}
          {hospital?.gstin && <div className="text-[11px] text-ink-muted">GSTIN: <span className="font-mono">{hospital.gstin}</span></div>}
          {hospital?.contact && !compact && <div className="text-[11px] text-ink-muted">{hospital.contact}</div>}
        </div>
      </div>
      {right && <div className="text-right text-[11px] text-ink-muted">{right}</div>}
    </div>
  );
}
